
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { Typography } from '@mui/material';
import { useFormActions } from '@ngt/forms-core';
import {
    CollaboratingGroupContext,
    FileUpload,
    FormOptionsContext,
    GroupedFields,
    GroupedTableArrayField,
    IFileUpload,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    PatientContext,
    Select,
    Text,
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { ComponentType, useMemo } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../api/dtos';


/*
 * ---------------------------------------------------------------------------------
 * constant
 * ---------------------------------------------------------------------------------
 */
const existingLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/view/${file?.id}`;
}

const existingDownloadLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/download/${file?.id}`;
}

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */
interface ISourceDocumentsProps {
    fileUploadView?: ComponentType<any>;
    ViewProps?: Record<string, any>;    
}

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const SourceDocuments: React.FunctionComponent<ISourceDocumentsProps> = ({
    fileUploadView,
    ViewProps
}) => {
    const { data: masterGroup } = React.useContext(MasterGroupContext);

    const { data: collaboratingGroup } = React.useContext(CollaboratingGroupContext);

    const { data: institution } = React.useContext(InstitutionContext);

    const { data: patient } = React.useContext(PatientContext);

    const { permissions: [ canAdministerOpms ] } = usePermissionsByIds([Permission.OpmsAdminister], masterGroup?.id, collaboratingGroup?.id, institution?.id, patient?.id);

    const { setFieldValue, submit } = useFormActions();

    const { readOnly } = React.useContext(FormOptionsContext);

    const onFileUploadChange = React.useCallback((value?: IFileUpload) => {
        if(readOnly && value?.upload != null) {
            setFieldValue('submitType', 'save');

            submit();
        }
    }, [setFieldValue, submit, readOnly]);

    const columns: Array<IGroupedTableArrayFieldColumn<Dtos.SourceDocument>> = useMemo(() => {
        return [
            {
                propertyPath: 'name',
                content: (
                    <Input component={Text} />
                )
            },
            {
                propertyPath: 'fileTypeId',
                content: (
                    <Input component={Select} />
                )
            },
            {
                propertyPath: 'file',
                content: (
                    <Input
                        component={FileUpload}
                        existingLink={existingLink}
                        existingDownloadLink={existingDownloadLink}
                        view={fileUploadView}
                        ViewProps={ViewProps}
                        allowRedactOnFormDisable={canAdministerOpms ?? false} 
                        onChange={onFileUploadChange}
                    />
                )
            }
        ]; 
    }, [fileUploadView, ViewProps, canAdministerOpms])

    return (
        <>
            <GroupedFields>
                <Typography
                    variant="h2"
                    sx={{ px: 2, pt: 3, pb: 1}}
                >
                    Source Documents
                </Typography>
                <GroupedTableArrayField
                    entityName="Document"
                    propertyPath="sourceDocuments"
                    columns={columns}
                    AddButtonProps={{
                        sx: {
                            mb: 2
                        }
                    }}
                />
            </GroupedFields>
        </>
    );
}

export default SourceDocuments;